import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllUsers } from '../../services/adminManagementServices';
import { Link } from 'react-router-dom';
import { AppDispatch } from '../../core/data/redux/store';
import * as Icon from 'react-feather';
import { Breadcrumb, Card, Button, Table, Tag, Input, Select, Space, Typography } from 'antd';

const { Title } = Typography;
const { Option } = Select;

const AllUsers = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { allUsers, isLoading, isError, errorMessage } = useSelector((state: any) => state.adminManagement);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');

  useEffect(() => {
    dispatch(getAllUsers());
  }, [dispatch]);

  const filteredUsers = allUsers.filter((user: any) => {
    const matchesSearch = (user.username?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (user.full_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (user.email_address?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const columns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (username: string) => (
        <Space>
          <div className="avatar avatar-sm" style={{ background: '#1890ff', color: '#fff', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {username ? username.charAt(0).toUpperCase() : '?'}
          </div>
          <span>{username || 'N/A'}</span>
        </Space>
      ),
    },
    {
      title: 'Full Name',
      dataIndex: 'full_name',
      key: 'full_name',
    },
    {
      title: 'Email',
      dataIndex: 'email_address',
      key: 'email_address',
      render: (email: string) => email || 'N/A',
    },
    {
      title: 'Phone',
      dataIndex: 'phone_number',
      key: 'phone_number',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={role === 'SUPER_ADMIN' ? 'red' : role === 'ADMIN' ? 'gold' : 'blue'}>{role}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (is_active: boolean) => (
        <Tag color={is_active ? 'green' : 'red'}>{is_active ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Verified',
      dataIndex: 'is_verified',
      key: 'is_verified',
      render: (is_verified: boolean) => (
        <Tag color={is_verified ? 'green' : 'orange'}>{is_verified ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, user: any) => (
        <Space>
          <Link to={`/admin/admin-management/admin-detail/${user.id}`}>
            <Button icon={<Icon.Eye size={16} />} size="small" />
          </Link>
          {user.role !== 'USER' && (
            <Link to={`/admin/admin-management/update-admin-role/${user.id}`}>
              <Button icon={<Icon.Edit size={16} />} size="small" />
            </Link>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper page-settings" style={{ paddingTop: 0 }}>
      <div className="content">
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <Link to="/admin/admin-management">Admin Management</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>All Users</Breadcrumb.Item>
        </Breadcrumb>
        <Card>
          <Title level={4} style={{ marginBottom: 24 }}>All Users</Title>
          <Space style={{ marginBottom: 16 }}>
            <Input
              placeholder="Search by username, name, or email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: 240 }}
              allowClear
            />
            <Select
              value={filterRole}
              onChange={setFilterRole}
              style={{ width: 160 }}
            >
              <Option value="all">All Roles</Option>
              <Option value="USER">User</Option>
              <Option value="ADMIN">Admin</Option>
              <Option value="SUPER_ADMIN">Super Admin</Option>
            </Select>
            <Button onClick={() => { setSearchTerm(''); setFilterRole('all'); }}>
              Clear Filters
            </Button>
          </Space>
          <Table
            columns={columns}
            dataSource={filteredUsers}
            loading={isLoading}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: (
              <div className="text-center py-4">
                <Icon.AlertCircle size={48} className="text-danger mb-3" />
                <Title level={5}>No users found</Title>
                <p style={{ color: '#888' }}>
                  {searchTerm || filterRole !== 'all'
                    ? 'Try adjusting your search or filter criteria'
                    : 'No users have been found'}
                </p>
              </div>
            ) }}
          />
        </Card>
      </div>
    </div>
  );
};

export default AllUsers; 